import React, { useEffect, useState } from "react"
import { getFilteredData } from "../util/getFilteredData"
import webify from "../util/webify"
import SVG from "react-inlinesvg"
import alertSvg from "../../../priv/static/svg/generic/alert-triangle.svg"

import { FilterState, parseQuery, toQuery } from "../models/FilterState"

type RecipientType = "passengers" | "account_holders" | "emergency_contacts" | "text_account_holders"

interface Props {
  initialRecipientType?: RecipientType
  passengerPartyIdIn?: number[]
  smsSignature: string
  emailSignature: string
  changeSmsSignaturePath: string
  fromEmail: string
  isStaff: boolean
  includeNonTravelingTripLeaders: boolean
}

function AnnouncementForm(props: Props) {
  let [recipientType, setRecipientType] = useState(props.initialRecipientType || "account_holders")
  let [includeNonTravelingTripLeaders, setIncludeNonTravelingTripLeaders] = useState(props.includeNonTravelingTripLeaders)

  useEffect(() => {
    let parsed = parseQuery(window.location.search)

    parsed.recipientType = recipientType
    if (parsed.custom && parsed.passengerPartyIdIn === undefined) {
      parsed.passengerPartyIdIn = []
    }

    parsed.includeNonTravelingTripLeaders = includeNonTravelingTripLeaders

    getFilteredData(toQuery(parsed))
  })

  return (
    <div className="form-grid-setup">
      <label className="text-gray-600 font-bold gc-uppercase">Communication Method</label>
      <div className="grid lg:grid-flow-col gap-2 -mt-3">
        <label className="radio-list" data-role="email-account-holders">
          <input
            type="radio"
            name="announcement[recipient_type]"
            value="account_holders"
            checked={recipientType === "account_holders"}
            onChange={() => setRecipientType("account_holders")}
          />{" "}
          Email Account Holders
        </label>

        <label className="radio-list" data-role="text-account-holders">
          <input
            type="radio"
            name="announcement[recipient_type]"
            value="text_account_holders"
            checked={recipientType === "text_account_holders"}
            onChange={() => setRecipientType("text_account_holders")}
          />{" "}
          Text Account Holders
        </label>

        <label className="radio-list" data-role="text-passengers">
          <input
            type="radio"
            name="announcement[recipient_type]"
            value="passengers"
            checked={recipientType === "passengers"}
            onChange={() => setRecipientType("passengers")}
          />{" "}
          Text Passengers
        </label>

        <label className="radio-list" data-role="text-emergency-contacts">
          <input
            type="radio"
            name="announcement[recipient_type]"
            value="emergency_contacts"
            checked={recipientType === "emergency_contacts"}
            onChange={() => setRecipientType("emergency_contacts")}
          />{" "}
          Text Emergency Contacts
        </label>
      </div>

      {recipientType === "account_holders" ? (
        <div>
          <label className="radio-list" data-role="include-non-traveling-trip-leaders">
            <input
              type="checkbox"
              name="announcement[include_non_traveling_trip_leaders]"
              checked={includeNonTravelingTripLeaders}
              value="true"
              onChange={(e) => setIncludeNonTravelingTripLeaders(e.target.checked)}
            />
            Include non-traveling trip leaders?
          </label>
        </div>
      ) : (
        <div className="p-2 border-gray-200 bg-gray-100">
          <SVG src={alertSvg} className="inline w-5 h-5 mr-2 text-gray-500" />
          Non traveling trip leaders can only receive email announcements.
        </div>
      )}

      {recipientType === "emergency_contacts" && (
        <div className="p-4 border-red-200 bg-red-100">
          <h3 className="text-gray-900 text-lg mb-2">Emergency Use Only</h3>
          <p className="max-w-para whitespace-normal">
            Using this option will notify emergency contacts listed for these passengers. These can be grandparents, other
            relatives, etc. Many of them may not even know about this trip.
          </p>
          <p>Please only use this feature responsibly, for emergencies only.</p>
        </div>
      )}

      {recipientType === "account_holders" ? <EmailFields {...props} /> : <SmsFields {...props} />}

      <div className="js-filtered-data">
        <div className="js-loading js-hidden text-center py-16 my-4 text-lg text-gray-800 bg-gray-100 italic">
          Loading recipients...
        </div>
        <div className="js-body" data-role="filtered-data"></div>
      </div>
    </div>
  )
}

interface FormFieldProps {
  label: string
  styles: string
  role: string
  id: string
  name: string
  type?: string | null
  maxLength?: number
}

function AnnouncementFormField(props: FormFieldProps) {
  return (
    <div className="grid gap-block-1">
      <label className="text-gray-600 font-bold gc-uppercase" htmlFor="announcement_text">
        {props.label}
      </label>
      <input
        autoComplete="off"
        className={props.styles}
        data-role={props.role}
        id={props.id}
        name={props.name}
        type={props.type || "text"}
        maxLength={props.maxLength}
        required
      />
    </div>
  )
}

function EmailFields(props: Props) {
  return (
    <>
      <input hidden={true} value={props.fromEmail} name="announcement[from]" readOnly />
      <div>
        <label className="text-gray-600 font-bold gc-uppercase" htmlFor="announcement_text">
          Reply-to email
        </label>
        <div className="">{props.fromEmail}</div>
      </div>

      {props.isStaff ? (
        <input hidden={true} value="Admin" name="announcement[sender_name]" readOnly />
      ) : (
        <AnnouncementFormField
          role="email-sender_name"
          label="Sender's Name *"
          styles="pl-2"
          id="announcement_sender_name"
          name="announcement[sender_name]"
          maxLength={255}
        />
      )}

      <AnnouncementFormField
        styles="pl-2"
        label="Email Subject *"
        role="email-subject"
        id="announcement_subject"
        name="announcement[subject]"
      />

      <div className="grid gap-block-1" key="2">
        <label className="text-gray-600 font-bold gc-uppercase" htmlFor="announcement_text">
          Email Text *
        </label>
        <textarea
          autoComplete="off"
          className="form-control pl-2"
          data-role="email-body"
          id="announcement_text"
          name="announcement[text]"
          placeholder="Hey everyone! Remember to sign all the required documents.&#10;-- John Doe"
          defaultValue={props.emailSignature}
          required
        />
      </div>

      <input hidden={true} value="email" name="announcement[delivery_type]" readOnly />
    </>
  )
}

function SmsFields(props: Props) {
  return (
    <>
      <div className="margin-v-xs" key="0">
        <div>
          <label className="label-styled" htmlFor="announcement_text">
            SMS Text *
          </label>
          <input
            autoComplete="off"
            className="form-control"
            data-role="text-message"
            id="announcement_text"
            name="announcement[text]"
            placeholder="Hey everyone! Remember to sign all the required documents. John Doe"
            type="text"
            defaultValue={props.smsSignature}
            required
          />
        </div>
      </div>
      <div className="text-gray-800 input-notice" key="1">
        {props.changeSmsSignaturePath && (
          <div>
            <span className="font-bold">Hint:</span> You can change the prepopulated signature&nbsp;
            <a data-role="change-signature-link" href={props.changeSmsSignaturePath}>
              here.
            </a>{" "}
            Also, if you hit <span className="italic">"enter"</span> on your keyboard the message will automatically send.
          </div>
        )}
      </div>
      <input key="2" hidden={true} value="sms" name="announcement[delivery_type]" readOnly />
    </>
  )
}

export default AnnouncementForm
webify("announcement-form", AnnouncementForm)
